import React, { useEffect, useState, createContext, useContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UserStateContext } from './user-state-context';
import { SocketContext } from './socket-context';
import { generateUUID } from './agent-context-utils/uuid-utils';
import { loadFromSessionStorage, saveToSessionStorage } from './agent-context-utils/storage-utils';
import { getAgentTypeLabel } from './agent-context-utils/agent-labels';
import { createMessageHandlers } from './agent-context-utils/agent-message-handlers';
import { createJourneyCanvas } from './agent-context-utils/journey-utils';
const AGENT_STORAGE_KEY = 'cinch.agentContext';
const MAX_PREVIOUS_PAGES = 5;
export const AgentContext = createContext();
export const AgentContextProvider = ({
  children
}) => {
  const {
    user,
    asCompany,
    token,
    flags
  } = useContext(UserStateContext);
  const {
    connectionStatus,
    sendMessage,
    subscribe,
    getConnectionInfo,
    showConsoleMessagesRef,
    toggleConsoleMessages
  } = useContext(SocketContext);
  const location = useLocation();

  // Navigation state
  const [navigationHistory, setNavigationHistory] = useState(() => loadFromSessionStorage(AGENT_STORAGE_KEY, {
    currentUrl: '',
    previousPages: []
  }, showConsoleMessagesRef?.current));

  // Interface state
  const [interfaceState, setInterfaceState] = useState({
    isVisible: true
  });
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Agent state
  const [agentType, setAgentType] = useState(null);
  const [hasSelectedAgent, setHasSelectedAgent] = useState(false);
  const [agentMessages, setAgentMessages] = useState([]);
  const [isAgentTyping, setIsAgentTyping] = useState(false);

  // Chat messages state
  const [messages, setMessages] = useState([]);
  const [streamingMessages, setStreamingMessages] = useState(new Map());

  // Artifact state
  const [artifacts, setArtifacts] = useState(new Map()); // filename -> artifact
  const [selectedArtifactFilename, setSelectedArtifactFilename] = useState(null);
  const [workspaceMode, setWorkspaceMode] = useState('code'); // 'code' | 'preview'

  // Derive selectedArtifact from the current artifacts map to ensure it's always up-to-date
  const selectedArtifact = selectedArtifactFilename ? artifacts.get(selectedArtifactFilename) : null;
  const [conversationId, setConversationId] = useState(() => generateUUID());

  // Store segment queries and template data by message ID
  const [segmentQueries, setSegmentQueries] = useState(new Map()); // messageId -> segment query data
  const [templateData, setTemplateData] = useState(new Map()); // messageId -> template data
  const [handoffTokenData, setHandoffTokenData] = useState(new Map()); // handoff message ID -> reference to data message ID
  const [journeyActions, setJourneyActions] = useState([]); // array of journey modification actions from agent

  // Token buffering refs for performance optimization
  const tokenBufferRef = useRef(new Map()); // messageId -> array of buffered tokens
  const bufferTimerRef = useRef(new Map()); // messageId -> setTimeout timer

  // Track the most recent data message IDs for linking with handoff tokens
  const mostRecentSegmentQueryIdRef = useRef(null);
  const mostRecentTemplateDataIdRef = useRef(null);

  // Window state handler - ref to store setCurrentWindowState from agent interface
  const setCurrentWindowStateRef = useRef(null);

  // Track navigation changes
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    setNavigationHistory(prev => {
      // If this is the same URL, don't add it again
      if (prev.currentUrl === currentPath) {
        return prev;
      }
      const newHistory = {
        currentUrl: currentPath,
        previousPages: prev.currentUrl ? [prev.currentUrl, ...prev.previousPages].slice(0, MAX_PREVIOUS_PAGES) : prev.previousPages
      };
      saveToSessionStorage(AGENT_STORAGE_KEY, newHistory, showConsoleMessagesRef?.current);
      return newHistory;
    });
  }, [location]);

  // Socket event handlers for agent messages
  useEffect(() => {
    if (!subscribe || !flags?.ai_chat_bot) return;
    if (showConsoleMessagesRef?.current) {
      console.log('Registering socket event handlers...');
    }

    // Create message handlers with all dependencies
    const handlers = createMessageHandlers({
      showConsoleMessagesRef,
      setSegmentQueries,
      setHandoffTokenData,
      setTemplateData,
      setStreamingMessages,
      setMessages,
      setIsAgentTyping,
      mostRecentSegmentQueryIdRef,
      mostRecentTemplateDataIdRef,
      tokenBufferRef,
      bufferTimerRef,
      setCurrentWindowStateRef,
      setWorkspaceModeCallback,
      setJourneyActions,
      journeyActions
    });
    const handleAgentMessage = handlers.handleAgentMessage;
    const handleAgentActionMessage = (data, callback) => {
      return handlers.handleAgentActionMessage(data, callback);
    };
    const handleAgentTyping = data => {
      return handlers.handleAgentTyping(data);
    };
    const handleAgentError = data => {
      return handlers.handleAgentError(data);
    };
    const handleUserInputRequest = data => {
      return handlers.handleUserInputRequest(data);
    };
    const handleAgentMessageToken = rawData => {
      return handlers.handleAgentMessageToken(rawData);
    };

    // Subscribe to socket events
    if (showConsoleMessagesRef?.current) {
      console.log('Subscribing to AGENT_MESSAGE...');
    }
    const unsubscribeMessage = subscribe('AGENT_MESSAGE', handleAgentMessage);
    if (showConsoleMessagesRef?.current) {
      console.log('Subscribing to agent_typing...');
    }
    // Subscribe to journey action events
    const unsubscribeAddNode = subscribe('ADD_JOURNEY_NODE', handleAgentActionMessage);
    const unsubscribeUpdateNode = subscribe('UPDATE_JOURNEY_NODE', handleAgentActionMessage);
    const unsubscribeDeleteNode = subscribe('DELETE_JOURNEY_NODE', handleAgentActionMessage);
    const unsubscribeAddLink = subscribe('ADD_JOURNEY_LINK', handleAgentActionMessage);
    const unsubscribeUpdateLink = subscribe('UPDATE_JOURNEY_LINK', handleAgentActionMessage);
    const unsubscribeDeleteLink = subscribe('DELETE_JOURNEY_LINK', handleAgentActionMessage);
    const unsubscribeTyping = subscribe('agent_typing', handleAgentTyping);
    if (showConsoleMessagesRef?.current) {
      console.log('Subscribing to agent_error...');
    }
    const unsubscribeError = subscribe('agent_error', handleAgentError);
    if (showConsoleMessagesRef?.current) {
      console.log('Subscribing to AGENT_MESSAGE_TOKEN...');
    }
    const unsubscribeToken = subscribe('AGENT_MESSAGE_TOKEN', handleAgentMessageToken);
    if (showConsoleMessagesRef?.current) {
      console.log('Subscribing to USER_INPUT_REQUEST...');
    }
    const unsubscribeUserInputRequest = subscribe('USER_INPUT_REQUEST', handleUserInputRequest);
    if (showConsoleMessagesRef?.current) {
      console.log('All event handlers registered successfully');
    }

    // Set up global mock handler for testing
    window._agentContextStreamingHandler = handleAgentMessageToken;
    return () => {
      unsubscribeMessage();
      unsubscribeTyping();
      unsubscribeError();
      unsubscribeToken();
      unsubscribeUserInputRequest();

      // Unsubscribe journey action events (old state-based)
      unsubscribeAddNode();
      unsubscribeUpdateNode();
      unsubscribeDeleteNode();
      unsubscribeAddLink();
      unsubscribeUpdateLink();
      unsubscribeDeleteLink();

      // Clean up global handlers
      delete window._agentContextStreamingHandler;
    };
  }, [subscribe, flags?.ai_chat_bot]);

  // Send message to agent with new payload format
  const sendToAgent = useCallback((content, contextData = null) => {
    if (!sendMessage) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Socket not available for sending message');
      }
      return false;
    }
    if (!agentType) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Agent type not defined');
      }
      return false;
    }
    if (!conversationId) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Conversation ID not available');
      }
      return false;
    }
    const payload = {
      payload: {
        content: content
      },
      metadata: {
        agent_pipeline: agentType,
        conversation_id: conversationId,
        navigation: navigationHistory,
        execution_context: {
          company_id: asCompany?.id,
          ...contextData // Pass additional context data if provided
        }
      }
    };
    if (showConsoleMessagesRef?.current) {
      console.log('Agent: Sending USER_INPUT message', payload);
    }
    try {
      return sendMessage('USER_INPUT', payload);
    } catch (error) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Error sending message via socket:', error);
      }
      return false;
    }
  }, [sendMessage, agentType, conversationId, asCompany]);

  // Clear agent messages
  const clearAgentMessages = useCallback(() => {
    setAgentMessages([]);
  }, []);

  // Toggle debug panel
  const toggleDebugPanel = useCallback(() => {
    setShowDebugPanel(prev => !prev);
  }, []);

  // Chat message functions
  const addMessage = useCallback(message => {
    setMessages(prev => [...prev, {
      ...message,
      id: message.id || Date.now(),
      timestamp: message.timestamp || Date.now()
    }]);
  }, []);

  // Artifact management functions
  const addArtifact = useCallback(artifact => {
    setArtifacts(prev => {
      const newMap = new Map(prev);
      newMap.set(artifact.filename, {
        ...artifact,
        is_user_edited: prev.has(artifact.filename) ? prev.get(artifact.filename).is_user_edited : false
      });
      return newMap;
    });
  }, []);
  const updateArtifact = useCallback((filename, updates) => {
    setArtifacts(prev => {
      const newMap = new Map(prev);
      console.log('[updateArtifact]: Called for', filename);
      if (newMap.has(filename)) {
        console.log('[updateArtifact]: Updating artifact:', filename, 'with', updates);
        newMap.set(filename, {
          ...newMap.get(filename),
          ...updates
        });
      }
      return newMap;
    });
  }, []);
  const markArtifactAsEdited = useCallback(filename => {
    updateArtifact(filename, {
      is_user_edited: true
    });
  }, [updateArtifact]);
  const selectArtifact = useCallback(filename => {
    const artifact = artifacts.get(filename);
    if (artifact) {
      if (showConsoleMessagesRef?.current) {
        console.log('[selectArtifact]: Selecting artifact:', filename);
        console.log('[selectArtifact]: Artifact content length:', artifact.content?.length);
      }
      setSelectedArtifactFilename(filename);
    }
  }, [artifacts]);
  const setWorkspaceModeCallback = useCallback(mode => {
    setWorkspaceMode(mode);
  }, []);

  // Generate new conversation ID when agent type changes
  const generateNewConversationId = useCallback(() => {
    const newId = generateUUID();
    setConversationId(newId);
    if (showConsoleMessagesRef?.current) {
      console.log('Agent: Generated new conversation ID:', newId);
    }
    return newId;
  }, []);
  const addAgentTypeMessage = useCallback(newAgentType => {
    addMessage({
      type: 'system',
      source: 'system',
      content: `${getAgentTypeLabel(newAgentType)} Agent Selected`,
      created_at: new Date().toISOString()
    });

    // Auto-create clean canvas for Journey Builder
    if (newAgentType === 'JOURNEY_BUILDER') {
      const journeyArtifact = createJourneyCanvas();
      addArtifact(journeyArtifact);
      selectArtifact('new_journey.json');
    }
  }, [addMessage, addArtifact, selectArtifact]);
  const addDisclaimerMessage = useCallback(() => {
    addMessage({
      type: 'system',
      source: 'system',
      content: 'Disclaimer: AI-generated content may contain errors or inaccuracies. Independently verify and cross-check the information for accuracy.',
      created_at: new Date().toISOString()
    });
  }, [addMessage]);
  const sendChatMessage = useCallback((content, context = null) => {
    // Add user message to chat
    addMessage({
      type: 'user',
      source: 'user',
      content: content,
      created_at: new Date().toISOString()
    });

    // Send to agent via socket with new payload format
    return sendToAgent(content, context);
  }, [addMessage, sendToAgent]);

  // Cancel ongoing generation
  const cancelGeneration = useCallback(() => {
    if (!sendMessage) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Socket not available for cancellation');
      }
      return false;
    }
    if (!conversationId) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Conversation ID not available for cancellation');
      }
      return false;
    }
    const payload = {
      payload: {},
      metadata: {
        conversation_id: conversationId
      }
    };
    if (showConsoleMessagesRef?.current) {
      console.log('Agent: Sending CANCEL', payload);
    }
    try {
      // Clear streaming messages
      // setStreamingMessages(new Map());
      setIsAgentTyping(false);
      return sendMessage('CANCEL', payload);
    } catch (error) {
      if (showConsoleMessagesRef?.current) {
        console.error('Agent: Error sending cancellation via socket:', error);
      }
      return false;
    }
  }, [sendMessage, conversationId]);

  // Get current agent context data
  const getAgentContext = () => {
    return {
      navigation: navigationHistory,
      /*      user: user
        ? {
            username: user.username || `${user.first_name} ${user.last_name}`.trim(),
            user_id: user.id,
            auth_token: token ? `${token.substring(0, 8)}...${token.substring(token.length - 8)}` : null,
          }
        : null,
      company: asCompany
        ? {
            company_name: asCompany.name,
            company_id: asCompany.id,
          }
        : null, */
      interface: interfaceState,
      agentType,
      showDebugPanel,
      socket: {
        connectionStatus,
        connected: connectionStatus === 'connected',
        info: getConnectionInfo ? getConnectionInfo() : null
      },
      agent: {
        messages: agentMessages,
        isTyping: isAgentTyping,
        messageCount: agentMessages.length
      },
      chat: {
        messages: messages,
        streamingMessages: streamingMessages,
        messageCount: messages.length + streamingMessages.size,
        conversationId: conversationId
      },
      workspace: {
        artifacts: artifacts,
        selectedArtifact: selectedArtifact,
        workspaceMode: workspaceMode,
        artifactCount: artifacts.size
      }
    };
  };
  const contextValue = {
    ...getAgentContext(),
    setInterfaceState,
    setAgentType: newType => {
      const oldType = agentType;
      setAgentType(newType);
      if (!hasSelectedAgent) {
        setHasSelectedAgent(true);
      }
      if (oldType !== newType) {
        // Generate new conversation ID for new agent type
        generateNewConversationId();
        addAgentTypeMessage(newType);
        addDisclaimerMessage();
      }
    },
    hasSelectedAgent,
    toggleDebugPanel,
    getAgentContext,
    sendToAgent,
    clearAgentMessages,
    // Chat functions
    addMessage,
    sendChatMessage,
    cancelGeneration,
    streamingMessages,
    messages,
    conversationId,
    generateNewConversationId,
    // Artifact functions
    addArtifact,
    updateArtifact,
    markArtifactAsEdited,
    selectArtifact,
    setWorkspaceMode: setWorkspaceModeCallback,
    artifacts,
    selectedArtifact,
    workspaceMode,
    showConsoleMessagesRef,
    toggleConsoleMessages,
    segmentQueries,
    templateData,
    handoffTokenData,
    journeyActions,
    clearJourneyActions: () => setJourneyActions([]),
    // Window state registration
    registerWindowStateSetter: setter => {
      setCurrentWindowStateRef.current = setter;
    },
    unregisterWindowStateSetter: () => {
      setCurrentWindowStateRef.current = null;
    }
  };
  return <AgentContext.Provider value={contextValue}>{children}</AgentContext.Provider>;
};